import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "./i18n";
import { track } from "./analytics";
import { JOURNEY_COPY, STORIES } from "./data/journey";
import BeforeAfter from "./BeforeAfter";
import "./ItemJourney.css";

const BA_COPY = {
  en: { before: "Before", after: "After", alt: "A Brooklyn curb piled with a couch and boxes, and the same curb clean after an Everything Eco pickup", caption: "Drag the handle — one pickup, same curb." },
  es: { before: "Antes", after: "Después", alt: "Una acera de Brooklyn llena de un sofá y cajas, y la misma acera limpia después de una recogida de Everything Eco", caption: "Arrastra el control — una recogida, la misma acera." },
};

/**
 * SECOND LIFE STORIES — real cinematic films of what happens after pickup:
 * the van clearing a curb, then a laptop / clothing / a toy each getting a
 * new life. Content lives in src/data/journey.ts.
 *
 * Videos are silent, lazy (preload=metadata), autoplay muted while on screen
 * and pause off screen. Reduced-motion users get posters with play controls
 * instead of autoplay. Captions are live HTML — nothing lives only in video.
 */

const ItemJourney: React.FC = () => {
  const { lang } = useLang();
  const rootRef = useRef<HTMLElement>(null);
  const [prm, setPrm] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setPrm(mq.matches);
    update();
    mq.addEventListener?.("change", update);
    return () => mq.removeEventListener?.("change", update);
  }, []);

  // Play only the videos that are actually on screen.
  useEffect(() => {
    if (prm) return;
    const root = rootRef.current;
    if (!root) return;
    const vids = Array.from(root.querySelectorAll("video"));
    const io = new IntersectionObserver(
      (es) => es.forEach((e) => {
        const v = e.target as HTMLVideoElement;
        if (e.isIntersecting) v.play().catch(() => {});
        else v.pause();
      }),
      { threshold: 0.25 }
    );
    vids.forEach((v) => io.observe(v));
    return () => io.disconnect();
  }, [prm]);

  const featured = STORIES.find((s) => s.wide) || STORIES[0];
  const rest = STORIES.filter((s) => s !== featured);

  const vidProps = prm
    ? { controls: true, preload: "metadata" as const }
    : { autoPlay: true, muted: true, loop: true, playsInline: true, preload: "metadata" as const };

  return (
    <section id="journey" className="ee-section ijv-section" ref={rootRef}>
      <div className="ee-container">
        <div className="ijv-head">
          <h2>{JOURNEY_COPY.title[lang]}</h2>
          <p>{JOURNEY_COPY.sub[lang]}</p>
        </div>

        {/* featured film — the pickup */}
        <figure className="ijv-featured">
          <video src={featured.video} poster={featured.poster} {...vidProps} />
          <figcaption>
            <strong>{featured.title[lang]}</strong>
            <span>{featured.caption[lang]}</span>
          </figcaption>
        </figure>

        {/* before / after — drag to compare */}
        <div className="ijv-ba">
          <BeforeAfter
            before="/ba-pickup-before.jpg"
            after="/ba-pickup-after.jpg"
            beforeLabel={BA_COPY[lang].before}
            afterLabel={BA_COPY[lang].after}
            alt={BA_COPY[lang].alt}
            caption={BA_COPY[lang].caption}
          />
        </div>

        {/* the second-life stories */}
        <div className="ijv-grid">
          {rest.map((s) => (
            <figure className="ijv-card" key={s.id}>
              <video src={s.video} poster={s.poster} {...vidProps} />
              <figcaption>
                <strong>{s.title[lang]}</strong>
                <span>{s.caption[lang]}</span>
              </figcaption>
            </figure>
          ))}
        </div>

        <div className="ijv-cta-row">
          <Link
            to="/pickup"
            className="ee-btn ee-btn-green"
            onClick={() => track("pickup_cta_clicked", { source: "item_journey" })}
          >
            {JOURNEY_COPY.cta[lang]}
          </Link>
          <span className="ijv-eligible">{JOURNEY_COPY.eligible[lang]}</span>
        </div>
      </div>
    </section>
  );
};

export default ItemJourney;
